import { forwardRef, ForwardRefExoticComponent, Ref } from 'react'
import { RenderNode } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { LinkButton } from '@/atoms/Button'
import { CaptionLG, HeadingXS, ParagraphSM, HeadingSM, TitleXS, EyebrowSM } from '@/atoms/Text/Text'
import { useProjectByPhaseContext } from '@/services/ProjectsService/ProjectProviders'
import { ICampaignAssets } from '@/types/generated-contentful'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { ExpressInterestDisclosure } from '@/views/ExpressInterestView/ExpressInterestDisclosure'
import TextBlock from '../Dynamic/TextBlock'

const descriptionOverride: RenderNode = {
  [BLOCKS.PARAGRAPH]: (_, children) => (
    <ParagraphSM as={(props) => <p {...props} />} className="md:text-center 2lg:text-left">
      {children}
    </ParagraphSM>
  ),
}

interface ExpressInterestCardProps {
  campaignAssets: ICampaignAssets
  className?: string
  displayDescription?: boolean
  displayTags?: boolean
  ref: Ref<null>
  theme?: 'dark' | 'light'
  isEmbed?: boolean
}

export const ExpressInterestCard: ForwardRefExoticComponent<ExpressInterestCardProps> = forwardRef(
  ({ campaignAssets, className, displayDescription, displayTags, theme = 'light', isEmbed = false }, ref) => {
    const { track } = useSafeAnalytics()
    const { query } = useRouter()
    const { totalUniqueInterestUsers } = useProjectByPhaseContext()

    const { locale = 'en' } = useLocale()
    const formatter = Intl.NumberFormat(locale, { minimumIntegerDigits: 1, maximumFractionDigits: 0 })

    const { name, shortDescription, tags } = campaignAssets.fields

    const { t } = useTranslate('torches')
    const expressInterest = t('expressInterest', 'Express Interest')
    const peopleExpressedInterest = t('peopleExpressedInterest', 'People expressed interest')

    const color = theme === 'dark' ? 'white' : 'gray-950'

    return (
      <div
        className={classNames(
          'w-full flex flex-col gap-6',
          {
            'text-gray-950': theme === 'light',
            'text-white': theme === 'dark',
          },
          className,
        )}
      >
        <div className="flex flex-col gap-5">
          <div className="md:text-center 2lg:text-left">
            <CaptionLG color={color} className="mb-2">
              {expressInterest}
            </CaptionLG>
            <HeadingXS color={color} weight="semibold">
              {name}
            </HeadingXS>
          </div>

          {displayTags && !!tags?.length && (
            <div className="flex gap-2 overflow-auto whitespace-nowrap scrollbar-none md:justify-center 2lg:justify-start">
              {tags.map((tag) => (
                <EyebrowSM key={tag.sys.id} color="core-gray-700" className="rounded bg-core-gray-200 p-2">
                  {tag.fields.title}
                </EyebrowSM>
              ))}
            </div>
          )}

          {displayDescription && shortDescription && (
            <TextBlock document={shortDescription} optionOverrides={descriptionOverride} />
          )}
        </div>

        <div className="md:text-center 2lg:text-left">
          <HeadingSM color={color} weight="semibold">
            {formatter.format(totalUniqueInterestUsers)}
          </HeadingSM>
          <TitleXS color={color}>{peopleExpressedInterest}*</TitleXS>
        </div>

        {!isEmbed && (
          <div ref={ref}>
            <LinkButton
              variant={theme === 'light' ? 'oxide' : 'oxide-bright'}
              href={`/torches/${query.torch}/express-interest`}
              onClick={() => track('Express Interest Button Clicked', { campaign: query.torch })}
            >
              {expressInterest}
            </LinkButton>
          </div>
        )}

        <ExpressInterestDisclosure theme={theme} />
      </div>
    )
  },
)

ExpressInterestCard.displayName = 'Express Interest Card'
